import * as XLSX from 'xlsx';

// Export shipments data to CSV
export const exportShipmentsToCSV = (shipments) => {
  if (!shipments || shipments.length === 0) {
    return;
  }

  // Define CSV headers including box information
  const headers = [
    'Shipment ID',
    'Customer Name',
    'Customer Phone',
    'Pickup Address',
    'Delivery Address',
    'City',
    'Route',
    'Box Type',
    'Box Quantity',
    'Assigned User',
    'Created By',
    'Status',
    'Shipment Status',
    'Payment Status',
    'Delivery Date',
    'Due Date',
    'Created Date'
  ];

  // Convert shipments to CSV rows - each row represents one box type
  const csvRows = [];
  
  shipments.forEach(shipment => {
    // If shipment has box information (from export API), use it directly
    if (shipment.box_type !== undefined) {
      csvRows.push([
        shipment.id,
        shipment.customer_name || '',
        shipment.customer_phone || '',
        shipment.pickup_address || '',
        shipment.delivery_address || '',
        shipment.city_name || '',
        shipment.route_name || '',
        shipment.box_type || '',
        shipment.box_quantity || '',
        shipment.assigned_user_name || '',
        shipment.created_by_name || '',
        shipment.status || '',
        shipment.shipment_status || '',
        shipment.shipment_payment_status || '',
        shipment.delivery_date ? new Date(shipment.delivery_date).toLocaleDateString() : '',
        shipment.due_date ? new Date(shipment.due_date).toLocaleDateString() : '',
        shipment.created_at ? new Date(shipment.created_at).toLocaleDateString() : ''
      ]);
    } else {
      // Fallback for shipments without box information
      csvRows.push([
        shipment.id,
        shipment.customer_name || '',
        shipment.customer_phone || '',
        shipment.pickup_address || '',
        shipment.delivery_address || '',
        shipment.city_name || '',
        shipment.route_name || '',
        'N/A', // box_type
        'N/A', // box_quantity
        shipment.assigned_user_name || '',
        shipment.created_by_name || '',
        shipment.status || '',
        shipment.shipment_status || '',
        shipment.shipment_payment_status || '',
        shipment.delivery_date ? new Date(shipment.delivery_date).toLocaleDateString() : '',
        shipment.due_date ? new Date(shipment.due_date).toLocaleDateString() : '',
        shipment.created_at ? new Date(shipment.created_at).toLocaleDateString() : ''
      ]);
    }
  });

  // Combine headers and rows
  const csvContent = [headers, ...csvRows]
    .map(row => row.map(field => `"${field}"`).join(','))
    .join('\n');

  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `shipments_with_boxes_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Export shipments data to Excel (XLSX) format
export const exportShipmentsToExcel = (shipments) => {
  if (!shipments || shipments.length === 0) {
    return;
  }

  // Define headers including box information
  const headers = [
    'Shipment ID',
    'Customer Name',
    'Customer Phone',
    'Pickup Address',
    'Delivery Address',
    'City',
    'Route',
    'Box Type',
    'Box Quantity',
    'Assigned User',
    'Created By',
    'Status',
    'Shipment Status',
    'Payment Status',
    'Delivery Date',
    'Due Date',
    'Created Date'
  ];

  // Convert shipments to rows - each row represents one box type
  // If a shipment has multiple box types, it will have multiple rows
  const rows = [];
  
  shipments.forEach(shipment => {
    // If shipment has box information (from export API), use it directly
    if (shipment.box_type !== undefined) {
      rows.push([
        shipment.id,
        shipment.customer_name || '',
        shipment.customer_phone || '',
        shipment.pickup_address || '',
        shipment.delivery_address || '',
        shipment.city_name || '',
        shipment.route_name || '',
        shipment.box_type || '',
        shipment.box_quantity || '',
        shipment.assigned_user_name || '',
        shipment.created_by_name || '',
        shipment.status || '',
        shipment.shipment_status || '',
        shipment.shipment_payment_status || '',
        shipment.delivery_date ? new Date(shipment.delivery_date) : '',
        shipment.due_date ? new Date(shipment.due_date) : '',
        shipment.created_at ? new Date(shipment.created_at) : ''
      ]);
    } else {
      // Fallback for shipments without box information (shouldn't happen in export mode)
      rows.push([
        shipment.id,
        shipment.customer_name || '',
        shipment.customer_phone || '',
        shipment.pickup_address || '',
        shipment.delivery_address || '',
        shipment.city_name || '',
        shipment.route_name || '',
        'N/A', // box_type
        'N/A', // box_quantity
        shipment.assigned_user_name || '',
        shipment.created_by_name || '',
        shipment.status || '',
        shipment.shipment_status || '',
        shipment.shipment_payment_status || '',
        shipment.delivery_date ? new Date(shipment.delivery_date) : '',
        shipment.due_date ? new Date(shipment.due_date) : '',
        shipment.created_at ? new Date(shipment.created_at) : ''
      ]);
    }
  });

  // Combine headers and rows
  const data = [headers, ...rows];

  // Create worksheet and workbook
  const ws = XLSX.utils.aoa_to_sheet(data);

  // Set date columns (Delivery Date, Due Date, Created Date) to date format mm/dd/yyyy
  const dateCols = [14, 15, 16]; // Updated indices for Delivery Date, Due Date, and Created Date
  for (let i = 1; i < data.length; i++) {
    dateCols.forEach(colIdx => {
      const cellRef = XLSX.utils.encode_cell({ r: i, c: colIdx });
      const cell = ws[cellRef];
      if (cell && cell.v instanceof Date) {
        cell.t = 'd';
        cell.z = 'mm/dd/yyyy';
      }
    });
  }

  // Set column widths for better readability
  const colWidths = [
    { wch: 12 }, // Shipment ID
    { wch: 20 }, // Customer Name
    { wch: 15 }, // Customer Phone
    { wch: 25 }, // Pickup Address
    { wch: 25 }, // Delivery Address
    { wch: 15 }, // City
    { wch: 15 }, // Route
    { wch: 15 }, // Box Type
    { wch: 12 }, // Box Quantity
    { wch: 18 }, // Assigned User
    { wch: 18 }, // Created By
    { wch: 18 }, // Status
    { wch: 18 }, // Shipment Status
    { wch: 15 }, // Payment Status
    { wch: 12 }, // Delivery Date
    { wch: 12 }, // Due Date
    { wch: 12 }  // Created Date
  ];
  ws['!cols'] = colWidths;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Shipments');

  // Write file
  XLSX.writeFile(wb, `shipments_with_boxes_${new Date().toISOString().split('T')[0]}.xlsx`);
}; 